import api from './api';

export interface Notification {
  id?: number;
  user_id?: number;
  enabled?: boolean;
  send_to_customer?: boolean;
  template_type?: 'email' | 'wechat';
  status?: string;
  notification_type?: 'trademark' | 'order' | 'payment';
  nation?: string;
  subject?: string;
  body?: string;
  service_type?: string;
  wechat_template_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface NotificationListParams {
  page?: number;
  per_page?: number;
  search?: string;
  nation?: string;
  status?: string;
  type?: string;
  priority?: string;
  unread_only?: boolean;
  service_type?: string;
  notification_type?: string;
  enabled?: boolean | string;
}

export interface NotificationListResponse {
  success: boolean;
  data: {
    data: Notification[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface NotificationResponse {
  success: boolean;
  message?: string;
  data?: Notification;
  errors?: any;
}

export interface UnreadCountResponse {
  success: boolean;
  unread_count: number;
}

export interface TestSendRequest {
  status: string;
  service_type: string;
  notification_type: 'trademark' | 'order' | 'payment';
  data_id: number;
  test_email: string;
}

export interface WechatTestSendRequest {
  subject: string;
  body: string;
  wechat_openid: string;
  wechat_template_id: string;
  service?: string;
  status?: string;
  notification_type?: 'trademark' | 'order' | 'payment';
  data_id?: number;
}

export interface TestSendResponse {
  success: boolean;
  message: string;
  data?: {
    sent_to?: string;
    wechat_sent_to?: string;
    email_sent_to?: string;
    template_id?: string;
    subject?: string;
    sent_at: string;
    email_success?: boolean;
    wechat_success?: boolean;
    errors?: string[];
  };
  errors?: any;
}

class NotificationService {
  // 获取通知列表
  async getNotifications(params?: NotificationListParams): Promise<NotificationListResponse> {
    const response = await api.get('/notifications', { params });
    return response.data;
  }

  // 创建通知
  async createNotification(notificationData: Notification): Promise<NotificationResponse> {
    const response = await api.post('/notifications', notificationData);
    return response.data;
  }

  // 获取通知详情
  async getNotification(id: number): Promise<NotificationResponse> {
    const response = await api.get(`/notifications/${id}`);
    return response.data;
  }

  // 更新通知
  async updateNotification(id: number, notificationData: Notification): Promise<NotificationResponse> {
    const response = await api.put(`/notifications/${id}`, notificationData);
    return response.data;
  }

  // 删除通知
  async deleteNotification(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  }

  // 批量删除通知模板
  async batchDeleteNotifications(ids: number[]): Promise<{ success: boolean; message: string; data?: { deleted_count: number } }> {
    const response = await api.post('/notifications/batch-delete', { ids });
    return response.data;
  }

  // 导出通知模板数据
  async exportNotifications(params?: NotificationListParams): Promise<Blob> {
    const response = await api.get('/notifications-export', {
      params,
      responseType: 'blob',
      headers: {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });
    return response.data;
  }

  // 导入通知模板数据
  async importNotifications(file: File): Promise<{ success: boolean; message: string; data?: any }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/notifications-import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // 标记通知为已读
  async markAsRead(id: number): Promise<NotificationResponse> {
    const response = await api.post(`/notifications/${id}/mark-as-read`);
    return response.data;
  }

  // 批量标记为已读
  async markAllAsRead(): Promise<{ success: boolean; message: string; count: number }> {
    const response = await api.post('/notifications/mark-all-as-read');
    return response.data;
  }

  // 获取未读通知数量
  async getUnreadCount(): Promise<UnreadCountResponse> {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  }

  // 发送通知
  async sendNotification(id: number): Promise<NotificationResponse> {
    const response = await api.post(`/notifications/${id}/send`);
    return response.data;
  }

  // 获取未读通知（快捷方法）
  async getUnreadNotifications(): Promise<NotificationListResponse> {
    return this.getNotifications({ unread_only: true });
  }

  // 测试发送邮件模板
  async testSendEmail(testData: TestSendRequest): Promise<TestSendResponse> {
    const response = await api.post('/notifications/test-send', testData);
    return response.data;
  }

  // 测试发送微信模板消息
  async testWechatSend(testData: WechatTestSendRequest): Promise<TestSendResponse> {
    const response = await api.post('/notifications/test-wechat-send', testData);
    return response.data;
  }

  // 渲染模板（根据数据ID替换变量）
  async testRender(data: { subject: string; body: string; notification_type: 'trademark'|'order'|'payment'; data_id: number; service_type?: string; status?: string }): Promise<{ success: boolean; data?: { subject: string; body: string }; message?: string }> {
    const response = await api.post('/notifications/test-render', data);
    return response.data;
  }

  // 获取数据库中已有的国家列表
  async getNations(): Promise<{ success: boolean; data: string[] }> {
    const response = await api.get('/notifications/nations');
    return response.data;
  }
}

const notificationService = new NotificationService();
export default notificationService;
